import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/api';
import { Settings as SettingsIcon, Percent, Package, Save, Loader2, Power, Calendar, Info, Zap } from 'lucide-react';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export default function AdminRoiSettings() {
  const [form, setForm] = useState({
    dailyRoiPercent: 1,
    roiActive: true,
    offDay: 0,
    minPackageAmount: 500,
    maxPackageAmount: 500000,
    packageStepAmount: 500
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState(null);
  const [triggering, setTriggering] = useState(false); // TEMP/TEST: manual ROI trigger button state

  const fetchSettings = () => {
    setLoading(true);
    api.get('/settings/roi')
      .then(r => {
        const { dailyRoiPercent, roiActive, offDay, minPackageAmount, maxPackageAmount, packageStepAmount, updatedAt } = r.data;
        setForm({ dailyRoiPercent, roiActive, offDay, minPackageAmount, maxPackageAmount, packageStepAmount });
        setMeta({ updatedAt });
      })
      .catch(err => toast.error(err.response?.data?.message || 'Failed to load settings'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchSettings(); }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      dailyRoiPercent: Number(form.dailyRoiPercent),
      roiActive: !!form.roiActive,
      offDay: Number(form.offDay),
      minPackageAmount: Number(form.minPackageAmount),
      maxPackageAmount: Number(form.maxPackageAmount),
      packageStepAmount: Number(form.packageStepAmount)
    };

    if (isNaN(payload.dailyRoiPercent) || payload.dailyRoiPercent < 0 || payload.dailyRoiPercent > 100) {
      return toast.error('Daily ROI % must be between 0 and 100');
    }
    if (payload.minPackageAmount > payload.maxPackageAmount) {
      return toast.error('Minimum package amount cannot be greater than maximum');
    }
    if (payload.packageStepAmount < 1) {
      return toast.error('Package step amount must be at least 1');
    }

    setSaving(true);
    try {
      const res = await api.put('/settings/roi', payload);
      toast.success(res.data.message);
      setMeta({ updatedAt: res.data.settings.updatedAt });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  // ================= TEMPORARY / TESTING ONLY =================
  // Manually runs today's ROI credit for all eligible users, instead of
  // waiting for the scheduled cron. Remove this handler + the button JSX
  // below (and the matching backend route) once testing is done.
  const handleTriggerRoiNow = async () => {
    if (!window.confirm('Yeh sabhi eligible users ke liye AAJ ki daily ROI turant credit kar dega (jaise cron karta hai). Continue?')) {
      return;
    }
    setTriggering(true);
    try {
      const res = await api.post('/income/admin/trigger-roi');
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to trigger ROI');
    } finally {
      setTriggering(false);
    }
  };
  // ================= END TEMPORARY / TESTING ONLY =================

  return (
    <DashboardLayout>
      <h1 className="page-title">ROI Settings</h1>
      <p className="page-subtitle">Control the daily ROI rate, ROI on/off day and package investment limits platform-wide</p>

      {/* ================= TEMPORARY / TESTING ONLY =================
          Manual "run ROI now" button. Delete this whole block later — it's
          just here so ROI can be tested without waiting for the cron time. */}
      <div className="alert alert-warning" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
        <span style={{ fontSize: '0.85rem' }}>
          <strong>Testing only:</strong> Manually run today's daily ROI credit right now for all eligible users (same as the automatic cron).
        </span>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleTriggerRoiNow}
          disabled={triggering}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
        >
          {triggering ? (
            <><Loader2 size={16} className="spin" /> Running...</>
          ) : (
            <><Zap size={16} /> Run Daily ROI Now</>
          )}
        </button>
      </div>
      {/* ================= END TEMPORARY / TESTING ONLY ================= */}

      {loading ? (
        <div className="loader-wrap"><div className="loader"></div></div>
      ) : (
        <div className="grid-2" style={{ gap: 24, alignItems: 'flex-start' }}>
          <div className="card">
            <div className="card-header">
              <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <SettingsIcon size={18} /> Configuration
              </h3>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Percent size={14} /> Daily ROI Percentage (%)</label>
                <div className="input-group">
                  <input
                    type="number"
                    name="dailyRoiPercent"
                    className="form-control"
                    value={form.dailyRoiPercent}
                    onChange={handleChange}
                    min={0}
                    max={100}
                    step={0.01}
                    required
                  />
                  <span className="input-icon">%</span>
                </div>
                <small style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                  This % of a user's active package amount is credited to their ROI wallet daily.
                </small>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Calendar size={14} /> Weekly Off Day (no ROI credited)</label>
                <select name="offDay" className="form-control" value={form.offDay} onChange={handleChange}>
                  {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Package size={14} /> Minimum Package Amount (₹)</label>
                <input
                  type="number"
                  name="minPackageAmount"
                  className="form-control"
                  value={form.minPackageAmount}
                  onChange={handleChange}
                  min={0}
                  required
                />
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Package size={14} /> Maximum Package Amount (₹)</label>
                <input
                  type="number"
                  name="maxPackageAmount"
                  className="form-control"
                  value={form.maxPackageAmount}
                  onChange={handleChange}
                  min={0}
                  required
                />
              </div>

              <div className="form-group">
                <label>Package Step / Multiple (₹)</label>
                <input
                  type="number"
                  name="packageStepAmount"
                  className="form-control"
                  value={form.packageStepAmount}
                  onChange={handleChange}
                  min={1}
                  required
                />
                <small style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                  Package amount entered by users must be a multiple of this value.
                </small>
              </div>

              <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input
                  type="checkbox"
                  id="roiActive"
                  name="roiActive"
                  checked={form.roiActive}
                  onChange={handleChange}
                  style={{ width: 18, height: 18 }}
                />
                <label htmlFor="roiActive" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Power size={14} /> ROI Generation Active
                </label>
              </div>

              {!form.roiActive && (
                <div className="alert alert-warning" style={{ fontSize: '0.82rem', marginBottom: 16 }}>
                  ROI generation is currently paused. The daily cron will run but will not credit any ROI until this is re-enabled.
                </div>
              )}

              <button type="submit" className="btn btn-primary btn-block" disabled={saving}>
                {saving ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Loader2 size={16} className="spin" /> Saving...</span>
                ) : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Save size={16} /> Save Settings</span>
                )}
              </button>

              {meta?.updatedAt && (
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 10, textAlign: 'center' }}>
                  Last updated: {new Date(meta.updatedAt).toLocaleString('en-IN')}
                </p>
              )}
            </form>
          </div>

          <div className="card" style={{ background: 'rgba(var(--primary-rgb),0.05)' }}>
            <div className="card-header">
              <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Info size={18} /> How this works
              </h3>
            </div>
            <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 12, color: 'var(--text-light)', fontSize: '0.88rem' }}>
              <li>▸ The daily ROI cron job (runs automatically) reads these values every time — no code change or redeploy needed.</li>
              <li>▸ Changing the % here only affects <strong>future</strong> ROI credits; past income entries are not recalculated.</li>
              <li>▸ New package activation requests are validated against the Min / Max / Step values here.</li>
              <li>▸ Turning off "ROI Generation Active" pauses ROI for everyone, platform-wide, instantly.</li>
              <li>▸ These same numbers are shown live to users on their "My Package" page.</li>
            </ul>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}